import {
  AppData,
  DocumentServiceError,
  DocumentServicesObject,
} from '@wix/document-services-types';
import { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import _ from 'lodash';
import { createTpaUtils } from '../../utils/tpaUtils';
import { t } from '../../../../../essentials/i18n';
import { isGuid } from '../../../../utils/guid';

const TYPE = {
  BENEFITS: 'tpaPanels.packagePicker.benefitsModal',
  CYCLES: 'tpaPanels.packagePicker.cyclesModal',
  EXTERNAL: 'tpaPanels.packagePicker.externalBenefitsModal',
  PACKAGES: 'tpaPanels.packagePicker.packagePickerModal',
};

interface UpgadeFlowOptions {
  shouldNavigate?: boolean;
  premiumIntent?: string;
  origin?: string;
}

interface ExtendedAppData extends AppData {
  notProvisioned?: boolean;
  demoMode?: boolean;
  openPremiumPage?: boolean;
  vendorProductId?: string;
  vendorProducts?: string[];
}

interface PackagePickerAppMarketAppData {
  name?: string;
  appIcon?: string;
  features?: any[];
  price: any;
  upgradeBenefits?: any[];
  appDefinitionId?: string;
  colorSvgIcon?: string;
  upgradeTitle?: string;
  upgradedToYearly?: boolean;
  vendorProductId?: string;
  packages?: Package[];
}

interface Package {
  id: string;
  name: string;
  price: string;
  is_active: boolean;
  freeMonth: boolean;
  monthly: PackagePeriod;
  yearly: PackagePeriod;
  oneTime: PackagePeriod;
  discountPercent: number;
}

interface PackagePeriod {
  price: string;
}

interface PackagePickerData {
  name?: string;
  appIcon?: string;
  colorSvgIcon?: string;
  packages?: Package[];
  features?: any[];
  price?: any;
  upgradeBenefits?: any[];
  upgradeTitle?: string;
  upgradedToYearly?: boolean;
  appDefinitionId?: string;
  vendorProductId?: string;
  panelName?: string;
  upgradeUrl?: string;
}

interface UpgradeUrlParams {
  appDefId: string;
  referralAdditionalInfo?: string;
  premiumIntent?: string;
}

export interface PackagePickerService {
  startUpgradeFlow: (
    appDefinitionId: string,
    options?: UpgadeFlowOptions,
  ) => void;
  isFullyUpgraded: (appData: ExtendedAppData) => boolean;
}

export const createPackagePickerService = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
): PackagePickerService => {
  const tpaUtils = createTpaUtils(platformApiMethods, documentServicesAPI);

  const isSaveSiteNeeded = (appData: ExtendedAppData): boolean => {
    const isDraftMode = documentServicesAPI.generalInfo.isDraft();
    return appData?.notProvisioned ?? (appData?.demoMode || isDraftMode);
  };

  const openUpgradePanel = (
    appData: ExtendedAppData,
    options: UpgadeFlowOptions,
  ) => {
    if (!appData.appDefinitionId) {
      throw new Error('appData is undefined');
    }
    const params: UpgradeUrlParams = {
      appDefId: appData.appDefinitionId,
      referralAdditionalInfo: options.origin,
    };

    const upgradeUrl =
      documentServicesAPI.tpa.billing.getSiteUpgradeUrl(params);

    const marketData = documentServicesAPI.tpa.appMarket.getData(
      appData.appDefinitionId,
    );

    const dashboardPanelOptions = {
      url: upgradeUrl,
      title: appData.isWixTPA
        ? t('Premium Plans')
        : t('upgrade_dashboard_modal.tpa.header', {
            App_name: marketData.name,
          }),
      shouldOverrideUrl: true,
      appDefinitionId: appData.appDefinitionId,
      shouldHideHelpButton: true,
    };

    platformApiMethods.editor.openDashboardPanel(
      appData,
      'TOKEN',
      dashboardPanelOptions,
    );
    platformApiMethods.editor.addAppToTrackForUpgrade?.(appData.applicationId!);
  };

  const saveAndContinueFlow = (
    appData: ExtendedAppData,
    options: UpgadeFlowOptions,
  ) => {
    const onSuccess = () => {
      openUpgradePanel(appData, options);
    };

    const onError = (error: DocumentServiceError) => {
      platformApiMethods.editor.showSavePublishFailAlert?.(
        appData,
        'openDashboard',
        { error },
      );
    };

    platformApiMethods.editor
      .saveInBackground('upgradeFlow', {})
      .then(onSuccess, onError);
  };

  const startUpgradeFlow = (
    appDefinitionId: string,
    options: UpgadeFlowOptions = {},
  ) => {
    if (!appDefinitionId) {
      throw new Error('appDefinitionId is required');
    }

    const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
      appDefinitionId,
    ) as ExtendedAppData;

    if (options.shouldNavigate !== false) {
      tpaUtils.selectComponentAndNavigateIfNeeded(appData);
    }

    if (!appData) {
      throw new Error(
        `appData was not found for appDefinitionId: ${appDefinitionId}`,
      );
    }

    if (isSaveSiteNeeded(appData)) {
      saveAndContinueFlow(appData, options);
    } else {
      openUpgradePanel(appData, options);
    }
  };

  const hasOnlyMonthlyPackages = (structure: PackagePickerData): boolean => {
    if (!structure?.packages?.length) {
      return true;
    }
    const lastProductPrice = structure.packages.at(-1)?.yearly?.price;
    return !lastProductPrice;
  };

  const isFreeApplication = (vendorProducts: string[] = []): boolean => {
    if (vendorProducts.length === 0) {
      return true;
    }
    return vendorProducts.length === 1 && vendorProducts[0] === 'basic';
  };

  const isMostExpensive = (
    structure: PackagePickerData,
    appData: ExtendedAppData,
  ): boolean => {
    if (!structure?.packages?.length) {
      return true;
    }
    const lastPackage = structure.packages.at(-1);
    if (!lastPackage) {
      return true;
    }
    const lastProductId = lastPackage.id;
    return lastProductId === appData.vendorProductId;
  };

  const pickPickerForm = (
    marketData: PackagePickerAppMarketAppData,
    appData: ExtendedAppData,
    options: { origin?: string; premiumIntent?: string } = {},
  ): PackagePickerData => {
    const relevantData: PackagePickerData = {
      name: marketData.name,
      appIcon: marketData.appIcon,
      colorSvgIcon: marketData.colorSvgIcon,
      packages: marketData.packages as any,
      features: marketData.features,
      price: marketData.price,
      upgradeBenefits: marketData.upgradeBenefits,
      upgradeTitle: marketData.upgradeTitle,
      upgradedToYearly: marketData.upgradedToYearly,
      appDefinitionId: marketData.appDefinitionId,
      vendorProductId: appData.vendorProductId,
    };

    relevantData.packages = (marketData.packages || []).filter(
      (p) => p.id !== 'oneTime',
    );

    if (appData?.openPremiumPage) {
      relevantData.panelName = TYPE.EXTERNAL;
      const params: UpgradeUrlParams = {
        appDefId: appData.appDefinitionId!,
        referralAdditionalInfo: options.origin,
      };
      if (options.premiumIntent) {
        params.premiumIntent = options.premiumIntent;
      }

      relevantData.upgradeUrl =
        documentServicesAPI.tpa.billing.getSiteUpgradeUrl(params);

      return relevantData;
    }

    relevantData.upgradeUrl = platformApiMethods.editor.info.getAppPremiumUrl?.(
      appData.appDefinitionId!,
    );

    return relevantData;
  };

  const isFullyUpgraded = (appData: ExtendedAppData): boolean => {
    if (!appData?.appDefinitionId) {
      throw new Error('app market call without appDefId');
    }

    if (!isGuid(appData.appDefinitionId)) {
      return true; // non guid apps are always internal.
    }

    const marketData = documentServicesAPI.tpa.appMarket.getData(
      appData.appDefinitionId,
    ) as PackagePickerAppMarketAppData;

    if (marketData) {
      const structure = pickPickerForm(marketData, appData);

      const isFreeApp = isFreeApplication(appData.vendorProducts || []);
      const isUpgraded = isFreeApp || !!appData.vendorProductId;
      const isYearly = !!marketData.upgradedToYearly;

      const fullUpgraded =
        isUpgraded &&
        isMostExpensive(structure, appData) &&
        (isYearly || hasOnlyMonthlyPackages(structure));

      return isFreeApp || fullUpgraded;
    }

    return true;
  };

  return {
    startUpgradeFlow,
    isFullyUpgraded,
  };
};
